"use client";

import { useState } from "react";
import NumberFlow from "@number-flow/react";
import { Icon } from "@/components/ui/icon";
import { PlanIcon } from "@/components/ui/plan-icon";
import { Button } from "@/components/ui/button";
import type { PlanResponse } from "../hooks/use-subscription";

const PLAN_FEATURES: Record<string, string[]> = {
  solo: [
    "1x Sender Mailbox",
    "AI Talent Search",
    "Arbitrator Evaluations",
    "Outreach Sequences",
    "In-App Sequences Inbox",
  ],
  growth: [
    "3x Sender Mailboxes",
    "1x Social Account",
    "Social Automation",
    "Omnichannel Unified Inbox",
    "Talent TAM Insights",
  ],
  business: [
    "6x Sender Mailboxes",
    "1x Social Account",
    "Sourcing Agents",
    "Usage Analytics",
    "ATS Integrations",
  ],
};

const PLAN_DESCRIPTIONS: Record<string, string> = {
  solo: "AI recruiting for the solo operator.",
  growth: "The stack for fast-moving teams.",
  business: "Built for teams hiring at full throttle.",
};

const PLAN_GRADIENTS: Record<string, string> = {
  solo: "rgba(84, 245, 237, 0.2)",
  growth: "rgba(255, 175, 83, 0.2)",
  business: "rgba(43, 93, 243, 0.2)",
};

interface PlanCardProps {
  plan: PlanResponse;
  cadence: "annual" | "monthly";
  currentCadence?: "annual" | "monthly";
  isCurrentPlan: boolean;
  currentSeatCount?: number;
  activeSeats?: number;
  isDowngrade?: boolean;
  isRecommended?: boolean;
  isUpgrading?: boolean;
  hideSeats?: boolean;
  onUpgrade: (planSlug: string, seatCount: number) => void;
  onSeatChange?: (newCount: number) => void;
  onBreakdownClick?: () => void;
}

export function PlanCard({
  plan,
  cadence,
  currentCadence,
  isCurrentPlan,
  currentSeatCount = 1,
  activeSeats = 0,
  isDowngrade = false,
  isRecommended = false,
  isUpgrading = false,
  hideSeats = false,
  onUpgrade,
  onSeatChange,
  onBreakdownClick,
}: PlanCardProps) {
  const maxSeats = plan.max_seats ?? 1;
  const initialSeats = isCurrentPlan
    ? currentSeatCount
    : Math.min(currentSeatCount, maxSeats);
  const [seats, setSeats] = useState(initialSeats);
  const cadenceChanged =
    isCurrentPlan && currentCadence != null && cadence !== currentCadence;
  const seatsChanged = isCurrentPlan && seats !== currentSeatCount;
  const isReducingSeats = isCurrentPlan && seats < currentSeatCount;
  const seatsBlocked = isReducingSeats && activeSeats > seats;
  const seatsToDeactivate = seatsBlocked ? activeSeats - seats : 0;

  const monthlyPrice = (plan.price_monthly_cents ?? 0) / 100;
  const annualMonthlyPrice =
    Math.round((plan.price_annual_cents ?? 0) / 12) / 100;
  const displayPrice = cadence === "annual" ? annualMonthlyPrice : monthlyPrice;

  const features = PLAN_FEATURES[plan.slug] ?? [];
  const displayCredits =
    cadence === "annual" ? plan.credits_per_seat * 12 : plan.credits_per_seat;
  const description = PLAN_DESCRIPTIONS[plan.slug] ?? "Full platform access.";
  const gradientColor = PLAN_GRADIENTS[plan.slug] ?? "rgba(255,255,255,0.1)";

  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-border bg-surface-primary dark:border-white/10 dark:bg-white/1">
      {/* Header: soft grid (masked fade) + plan color wash.
          Avoid mix-blend — it flashes under parent opacity enter animations. */}
      <div className="relative border-b border-border p-4 dark:border-white/10">
        <div
          className="pointer-events-none absolute inset-0 opacity-50 [background-image:linear-gradient(rgba(0,0,0,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.05)_1px,transparent_1px)] dark:[background-image:linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)]"
          style={{
            backgroundSize: "23px 23px",
            maskImage:
              "linear-gradient(to bottom, transparent 0%, #000 20%, #000 100%)",
            WebkitMaskImage:
              "linear-gradient(to bottom, transparent 0%, #000 20%, #000 100%)",
          }}
        />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background: `linear-gradient(to top, ${gradientColor}, transparent 65%)`,
          }}
        />

        <div className="relative flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <PlanIcon slug={plan.slug} width={24} height={24} />
              <span className="text-base font-medium tracking-[-0.48px] text-heading dark:text-white">
                {plan.name}
              </span>
            </div>
            <span className="text-[11px] font-medium text-contrast/50 dark:text-white/50">
              Up to {maxSeats} {maxSeats === 1 ? "seat" : "seats"}
            </span>
          </div>

          <p className="text-[13px] leading-snug text-contrast/60 dark:text-white/70">
            {description}
          </p>

          <div>
            <span className="text-[28px] font-semibold leading-none text-heading dark:text-white">
              $<NumberFlow value={displayPrice} trend={0} />
            </span>
            <p className="mt-0.5 text-[13px] text-contrast/50 dark:text-white/50">
              per seat/month, billed{" "}
              {cadence === "annual" ? "annually" : "monthly"}
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3 border-b border-border p-4 dark:border-white/10">
        {!hideSeats && (
          <div className="flex items-center justify-between">
            <span className="text-[13px] text-contrast/60 dark:text-white/70">
              Seats
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSeats(Math.max(1, seats - 1))}
                disabled={seats <= 1 || maxSeats <= 1}
                className="flex size-6 cursor-pointer items-center justify-center rounded border border-border bg-contrast/4 text-contrast/70 transition-colors hover:bg-contrast/8 disabled:cursor-not-allowed disabled:opacity-30 dark:border-white/10 dark:bg-white/4 dark:text-white/70 dark:hover:bg-white/8"
              >
                <Icon name="IconMinusSmall" size={12} />
              </button>
              <span className="w-4 text-center text-sm font-medium text-heading dark:text-white">
                <NumberFlow value={seats} trend={0} />
              </span>
              <button
                type="button"
                onClick={() => setSeats(Math.min(maxSeats, seats + 1))}
                disabled={seats >= maxSeats || maxSeats <= 1}
                className="flex size-6 cursor-pointer items-center justify-center rounded border border-border bg-contrast/4 text-contrast/70 transition-colors hover:bg-contrast/8 disabled:cursor-not-allowed disabled:opacity-30 dark:border-white/10 dark:bg-white/4 dark:text-white/70 dark:hover:bg-white/8"
              >
                <Icon name="IconPlusSmall" size={12} />
              </button>
            </div>
          </div>
        )}

        <Button
          onClick={() => {
            if (isCurrentPlan && (cadenceChanged || seatsChanged)) {
              if (cadenceChanged) {
                onUpgrade(plan.slug, seats);
              } else if (seatsChanged && onSeatChange) {
                onSeatChange(seats);
              }
            } else if (!isCurrentPlan) {
              onUpgrade(plan.slug, seats);
            }
          }}
          disabled={
            isUpgrading || (isCurrentPlan && !seatsChanged && !cadenceChanged)
          }
          variant={isRecommended && !isCurrentPlan ? "light" : "secondary"}
          size="sm"
          className="w-full"
        >
          {isUpgrading ? (
            <>
              {isCurrentPlan
                ? "Updating"
                : isDowngrade
                  ? "Downgrading"
                  : "Upgrading"}{" "}
              to {plan.name}
              <Icon
                name="IconLoadingCircle"
                size={14}
                className="animate-spin"
              />
            </>
          ) : isCurrentPlan ? (
            cadenceChanged && seatsChanged ? (
              `Switch to ${cadence === "annual" ? "annual" : "monthly"} with ${seats} ${seats === 1 ? "seat" : "seats"}`
            ) : cadenceChanged ? (
              `Switch to ${cadence === "annual" ? "annual" : "monthly"}`
            ) : seatsChanged ? (
              `${seats < currentSeatCount ? "Downgrade" : "Update"} to ${seats} ${seats === 1 ? "seat" : "seats"}`
            ) : (
              "Current Plan"
            )
          ) : (
            `${isDowngrade ? "Downgrade" : "Upgrade"} to ${plan.name}`
          )}
        </Button>
      </div>

      <div className="flex flex-col gap-3 p-4">
        <div className="flex items-center gap-1.5">
          <Icon
            name="IconCheckCircle2"
            size={14}
            className="shrink-0 text-contrast/50 dark:text-white/50"
          />
          <span className="text-[13px] tracking-[-0.26px] text-contrast/70 dark:text-white/80">
            <NumberFlow value={displayCredits} locales="en-US" /> Credits
          </span>
          <button
            type="button"
            onClick={onBreakdownClick}
            className="shrink-0 cursor-pointer"
          >
            <Icon
              name="IconCircleInfo"
              size={12}
              className="text-contrast/40 dark:text-white/50"
            />
          </button>
        </div>
        {features.map((feature) => (
          <div key={feature} className="flex items-center gap-1.5">
            <Icon
              name="IconCheckCircle2"
              size={14}
              className="shrink-0 text-contrast/50 dark:text-white/50"
            />
            <span className="text-[13px] tracking-[-0.26px] text-contrast/70 dark:text-white/80">
              {feature}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
